import { ADD_ON_IDS, type AddOnsVO, type Rating, type Suspend } from '@ctct/domain-layer';
import { AccountModificationFlowId, NumberFormat } from '@ctct/infrastructure-layer';

import { SharedOrderItemsTranslationService } from './SharedOrderItemsTranslationService';
import type { TranslationGetter } from '../../../types';
import { formatTodaysTotal } from '../../_shared/OrderSummary/utils/OrderTotal';
import { orderSummaryEventBus } from '../OrderSummaryEventBus';
import type { FooterRow, OrderItemProps, OrderItemsService, OrderSummaryPageStore } from '../types';

export interface CustomerOrderItemsServiceParams {
  pageStore: OrderSummaryPageStore;
  t: TranslationGetter;
  rating: Rating | undefined;
  addOns: AddOnsVO | undefined;
  flowId: AccountModificationFlowId;
  suspend?: Suspend | null;
  isStripeCustomer: boolean;
  isSocialStarter: boolean;
  isNonProfit?: boolean | undefined;
}

export class CustomerOrderItemsService implements OrderItemsService {
  private readonly CONTINUE_PAYMENT_PLAN = 0;

  private pageStore: OrderSummaryPageStore;

  private t: TranslationGetter;

  private rating: Rating | undefined;

  private addOns: AddOnsVO | undefined;

  private flowId: AccountModificationFlowId;

  private suspend?: Suspend | null;

  private isStripeCustomer: boolean;

  private isSocialStarter: boolean;

  private isSuspended: boolean;

  private sharedTranslationService: SharedOrderItemsTranslationService;

  constructor({
    pageStore,
    t,
    rating,
    addOns,
    flowId,
    suspend,
    isStripeCustomer,
    isSocialStarter,
    isNonProfit,
  }: CustomerOrderItemsServiceParams) {
    this.pageStore = pageStore;
    this.t = t;
    this.rating = rating;
    this.addOns = addOns;
    this.flowId = flowId;
    this.suspend = suspend;
    this.isStripeCustomer = isStripeCustomer;
    this.isSocialStarter = isSocialStarter;
    this.isSuspended = Boolean(this.suspend);
    this.sharedTranslationService = new SharedOrderItemsTranslationService(t, isStripeCustomer, isNonProfit);
  }

  public api() {
    return {};
  }

  public data() {
    return {
      getCopies: () => {
        return {
          heading: this.t('application-layer.order-summary.heading'),
          loadSpinner: this.t('application-layer.order-summary.load-spinner'),
        };
      },

      getOrderRows: (): OrderItemProps[] => {
        if (!this.rating) {
          return [];
        }

        const planRow = this.getProratedPlanRow(this.rating);
        const newPlanRow = this.isStripeCustomer ? null : this.getNewPlanRow(this.rating);
        const addOnRows = this.getAddOnRows(this.rating);

        return [planRow, newPlanRow, ...addOnRows].flatMap((row) => (row ? [row] : []));
      },

      getFooterRows: ({
        isVatEu,
        isB2bNotificationVisible,
      }: {
        isVatEu: boolean;
        isB2bNotificationVisible: boolean;
      }): FooterRow[] => {
        if (!this.rating) {
          return [];
        }

        const subtotalBeforeTaxRow = this.getSubtotalBeforeTaxRow(this.rating);
        const discountRows = this.getProratedPlanDiscountRows(this.rating);
        const taxRow = this.getTaxRow(this.rating, isVatEu, isB2bNotificationVisible);
        const amountAppliedFromBalanceRows = this.getAmountAppliedFromBalanceRows(this.rating);
        const amountFromOverdueBalanceRow = this.getAmountFromOverdueBalance(this.rating);
        const todayTotalRow = this.getTodaysTotalRow(this.rating);

        return [
          subtotalBeforeTaxRow,
          ...discountRows,
          taxRow,
          ...amountAppliedFromBalanceRows,
          amountFromOverdueBalanceRow,
          todayTotalRow,
        ].flatMap((row) => (row ? [row] : []));
      },

      getSubfooterRows: () => {
        if (!this.rating) {
          return [];
        }

        if (this.isStripeCustomer) {
          return [];
        }

        const nextMonthPrice = this.getNextMonthPriceRow(this.rating);
        const nextMonthTaxPrice = this.getNextMonthTaxRow(this.rating);
        const nextMonthDiscountRow = this.getNextMonthDiscountRow(this.rating);
        const nextMonthAccountBalanceRows = this.getNextMonthAccountBalanceRows(this.rating);
        const nextMonthTotalPriceRow = this.getNextMonthTotalPriceRow(this.rating);

        return [
          nextMonthPrice,
          nextMonthTaxPrice,
          nextMonthDiscountRow,
          ...nextMonthAccountBalanceRows,
          nextMonthTotalPriceRow,
        ].flatMap((row) => (row ? [row] : []));
      },

      getTodaysTotal: () => {
        return formatTodaysTotal(this.rating);
      },
    };
  }

  public handlers() {
    return {
      onRemoveAddOnClick: (addOnId: string) => {
        orderSummaryEventBus.emit('addOnRemoved', addOnId, { source: 'user' });
      },

      onEditAddOnClick: (addOnId: string) => {
        orderSummaryEventBus.emit('editAddOnClicked', addOnId, { source: 'user' });
      },

      onEditPlanClick: (planId: string) => {
        orderSummaryEventBus.emit('editPlanClicked', planId, { source: 'user' });
      },
    };
  }

  private getProratedPlanRow(rating: Rating) {
    const plan = rating.getPlan();
    const hasDiscount = rating.getPlanPrepayDiscount().priceAmount > 0;

    const { tierMin, tierMax } = plan.currentTier;

    const mainLabel =
      this.isSuspended || this.isSocialStarter
        ? rating.getPlanName()
        : this.t('application-layer.order-summary.prorated-plan-charge');

    const contactsTierLabel = this.sharedTranslationService.getContactsTierLabel(tierMin, tierMax);

    const secondaryLabels = [
      `${this.isSuspended ? contactsTierLabel : `${rating.getPlanName()} (${contactsTierLabel})`}`,
    ];

    const secondaryLabelsSocialStarter = this.getPlanSecondaryLabels(rating, rating.getPlan());

    // For Stripe customers, include discount label in prorated plan row
    // since newPlanRow is null for Stripe
    const savingsDiscountKey = this.sharedTranslationService.getSavingsDiscountKey();

    return {
      id: `prorated-plan-${plan.planId}`,
      variant: 'bold' as const,
      mainLabel,
      secondaryLabels: this.isSocialStarter ? secondaryLabelsSocialStarter : secondaryLabels,
      priceLabel: rating.getProratedPrice().format(rating.getCurrency()),
      buttons: [],
      ...(this.isStripeCustomer && hasDiscount
        ? {
            discountLabel: this.t(savingsDiscountKey),
            discountPriceLabel: `-${rating.getPlanPrepayDiscount().format(rating.getCurrency())}`,
          }
        : {}),
    };
  }

  private getNewPlanRow(rating: Rating) {
    const handlers = this.handlers();
    const plan = rating.getPlan();
    const hasDiscount = rating.getPlanPrepayDiscount().priceAmount > 0;
    const shouldHideEditPlan = this.flowId === AccountModificationFlowId.ChangePaymentPlan;

    if (this.getIsContinuingPlan(rating) || this.isSocialStarter) {
      return null;
    }

    const editPlanButton = {
      label: this.t('application-layer.order-summary.edit-plan'),
      onClick: () => handlers.onEditPlanClick(String(plan.planId)),
      disabled: this.pageStore.getState().orderSummaryDisabled,
    };

    // This method is only called for BaaS customers (line 87: isStripeCustomer ? null : getNewPlanRow),
    // so "prepay" is always the correct term here.
    const paymentTermKey = 'application-layer.order-summary.prepay';
    const savingsDiscountKey = this.sharedTranslationService.getSavingsDiscountKey();

    return {
      id: String(plan.planId),
      variant: 'bold' as const,
      mainLabel: this.isSuspended ? `${rating.getPlanName()} (${this.t(paymentTermKey)})` : rating.getPlanName(),
      secondaryLabels: this.getPlanSecondaryLabels(rating, plan),
      priceLabel: rating.getPlanBaseTotal().format(rating.getCurrency()),
      buttons: shouldHideEditPlan ? [] : [editPlanButton],
      ...(hasDiscount
        ? {
            discountLabel: this.t(savingsDiscountKey),
            discountPriceLabel: `-${rating.getPlanPrepayDiscount().format(rating.getCurrency())}`,
          }
        : {}),
    };
  }

  private getAddOnRows(rating: Rating) {
    const newAddons = rating.getAddons().filter((addon) => !rating.getExistingAddOnServices().includes(addon.id));
    const existingAddons = rating.getAddons().filter((addon) => rating.getExistingAddOnServices().includes(addon.id));

    const existingAddonsRows = (existingAddons || []).map((addon) => {
      const addonHasDiscount = addon.monthlyDiscount.priceAmount > 0;

      if (this.isFreeSmsAddon(addon)) {
        return {
          id: addon.id,
          mainLabel: this.getAddOnMainLabel(addon),
          secondaryLabels: [],
          priceLabel: this.t('application-layer.order-summary.addon-included-in-plan'),
        };
      }

      // Use subscription or prepay language based on Stripe flag
      const addonSavingsDiscountKey = this.sharedTranslationService.getSavingsDiscountKey();

      if (this.isSuspended) {
        return {
          id: addon.id,
          mainLabel: this.getAddOnMainLabel(addon),
          secondaryLabels: [],
          priceLabel: String(addon.proRatedTotal.format(rating.getCurrency())),
          ...(addonHasDiscount
            ? {
                discountLabel: this.t(addonSavingsDiscountKey),
                discountPriceLabel: this.getAddOnDiscountPriceLabel(rating, addon),
              }
            : {}),
        };
      }

      // do not show add-ons for continuing payment plan
      if (this.getIsContinuingPlan(rating)) {
        return null;
      }

      return {
        id: addon.id,
        mainLabel: this.getAddOnMainLabel(addon),
        secondaryLabels: this.getAddonSecondaryLabels(rating, addon),
        priceLabel: String(addon.fullPrice.format(rating.getCurrency())),
        ...(addonHasDiscount
          ? {
              discountLabel: this.t(addonSavingsDiscountKey),
              discountPriceLabel: this.getAddOnDiscountPriceLabel(rating, addon),
            }
          : {}),
      };
    });

    const newAddonsRows = (newAddons || []).map((addon) => {
      // for new addons - we only want to show free SMS addon as included in premium plan
      if (!this.isFreeSmsAddon(addon)) {
        return null;
      }

      return {
        id: addon.id,
        mainLabel: this.getAddOnMainLabel(addon),
        secondaryLabels: [],
        priceLabel: this.t('application-layer.order-summary.addon-included-in-plan'),
      };
    });

    return [...existingAddonsRows, ...newAddonsRows].flatMap((row) => (row ? [row] : []));
  }

  private getSubtotalBeforeTaxRow(rating: Rating) {
    const baseSubtotal = rating
      .getProratedTotalPrice()
      .add(this.isSuspended ? rating.getAddOnsTotalProRated() : rating.getAddOnsTotal())
      .substract(rating.getProratedPrepayDiscountAmount());

    // for stripe and social starter customers we do not want to show the new plan row, so we do not include it in the subtotal
    const priceLabel = (
      this.isStripeCustomer || this.isSocialStarter ? baseSubtotal : baseSubtotal.add(rating.getPlanTotal())
    ).format(rating.getCurrency());

    return {
      id: 'subtotal-before-tax',
      label: this.t('application-layer.order-summary.subtotal-before-tax'),
      priceLabel,
    };
  }

  private getTaxRow(rating: Rating, isVatEu: boolean, isB2bNotificationVisible: boolean): FooterRow {
    const priceLabel = rating.getTotalTax().format(rating.getCurrency());

    return {
      id: 'estimated-taxes',
      label: isVatEu
        ? this.t('application-layer.order-summary.vat-zero-percent')
        : this.t('application-layer.order-summary.estimated-taxes'),
      priceLabel,
      tooltipI18nKey: isB2bNotificationVisible ? this.t('application-layer.order-summary.tax-b2b-tooltip') : undefined,
    };
  }

  private getAmountAppliedFromBalanceRows(rating: Rating) {
    if (rating.getAmountAppliedFromBalance().priceAmount === 0) {
      return [];
    }

    return [
      {
        id: 'applied-from-balance',
        variant: 'discount' as const,
        label: this.t('application-layer.order-summary.applied-from-balance'),
        priceLabel: `-${rating.getAmountAppliedFromBalance().format(rating.getCurrency())}`,
      },
      {
        id: 'account-balance-after-payment',
        label: this.t('application-layer.order-summary.account-balance-after-payment'),
        priceLabel: rating.getFinalBalance().format(rating.getCurrency()),
      },
    ];
  }

  private getAmountFromOverdueBalance(rating: Rating) {
    const overdueAmount = rating.getOverdueAmount();

    if (!overdueAmount) {
      return null;
    }

    return {
      id: 'included-from-remaining-balance',
      label: this.t('application-layer.order-summary.account-balance-due'),
      priceLabel: overdueAmount.format(rating.getCurrency()),
    };
  }

  private getTodaysTotalRow(rating: Rating) {
    return {
      id: 'todays-total',
      variant: 'bold' as const,
      label: this.t('application-layer.order-summary.todays-total'),
      priceLabel: formatTodaysTotal(rating),
    };
  }

  private getNextMonthPriceRow(rating: Rating) {
    // if no discount account balance contribution and tax, we can skip this row, to just show total price
    if (
      rating.getMonthlyDiscountPrice().priceAmount === 0 &&
      rating.getBalanceContributionToNextPayment().priceAmount === 0 &&
      rating.getMonthlyTaxPrice().priceAmount === 0
    ) {
      return null;
    }

    const priceLabel = rating.getMonthlyBasePrice().format(rating.getCurrency());

    return {
      id: 'next-month-price',
      label: this.t('application-layer.order-summary.next-month-price'),
      priceLabel,
    };
  }

  private getNextMonthTaxRow(rating: Rating) {
    if (rating.getMonthlyTaxPrice().priceAmount === 0) {
      return null;
    }

    const priceLabel = rating.getMonthlyTaxPrice().format(rating.getCurrency());

    return {
      id: 'next-month-tax',
      label: this.t('application-layer.order-summary.next-month-tax'),
      priceLabel,
    };
  }

  private getNextMonthDiscountRow(rating: Rating) {
    if (rating.getMonthlyDiscountPrice().priceAmount === 0) {
      return null;
    }

    const priceLabel = rating.getMonthlyDiscountPrice().format(rating.getCurrency());

    return {
      id: 'next-month-discount',
      variant: 'discount' as const,
      label: this.t('application-layer.order-summary.next-month-discount'),
      priceLabel: `-${priceLabel}`,
    };
  }

  private getNextMonthAccountBalanceRows(rating: Rating) {
    if (rating.getFinalBalance().priceAmount <= 0) {
      return [];
    }

    return [
      {
        id: 'next-month-applied-from-balance',
        variant: 'discount' as const,
        label: this.t('application-layer.order-summary.applied-from-balance'),
        priceLabel: `-${rating.getBalanceContributionToNextPayment().format(rating.getCurrency())}`,
      },
      {
        id: 'next-month-account-balance-after-payment',
        label: this.t('application-layer.order-summary.account-balance-after-payment'),
        priceLabel: rating.getBalanceAfterNextPayment().format(rating.getCurrency()),
      },
      {
        id: 'next-month-total',
        variant: 'bold' as const,
        label: this.t('application-layer.order-summary.next-month-total-price'),
        priceLabel: rating.getNextPaymentTotalAfterBalanceContribution().format(rating.getCurrency()),
      },
    ];
  }

  private getNextMonthTotalPriceRow(rating: Rating) {
    // if  Account Balance is contributing to next moth payment, Total price will be returned by this.getNextMonthAccountBalanceRows, so we do not want to duplicate it here
    if (rating.getFinalBalance().priceAmount > 0) {
      return null;
    }

    const priceLabel = rating.getMonthlyTotalPrice().format(rating.getCurrency());

    return {
      id: 'next-month-total',
      variant: 'bold' as const,
      label: this.t('application-layer.order-summary.next-month-total-price'),
      priceLabel,
    };
  }

  private getProratedPlanDiscountRows(rating: Rating) {
    return rating
      .getNewAppliedDiscountCodes()
      .map((promoCodeVO) => {
        if (promoCodeVO.getProratedPromotionCredit().priceAmount === 0) {
          return null;
        }

        const promotionValue = promoCodeVO.getProratedPromotionCredit().format(rating.getCurrency());
        const productName = this.t('application-layer.order-summary.your-order');

        return {
          id: promoCodeVO.getCode(),
          variant: 'discount' as const,
          label: this.t('application-layer.order-summary.discount-label', { promotionValue, productName }),
          priceLabel: `-${promotionValue}`,
        };
      })
      .flatMap((row) => (row ? [row] : []));
  }

  private getAddOnMainLabel(addon: ReturnType<Rating['getAddons']>[0]) {
    if (addon.id !== 'CTCT-SMS-ADDON') {
      return addon.name;
    }

    const currentlyUsedSms = addon.usage?.used || 0;
    let maxSmsNumberForCurrentTier;

    if (this.addOns) {
      const smsAddOn = (this.addOns.getAddOns() || []).find((a) => a.getId() === addon.id);
      const priceTiers = smsAddOn?.priceTiers || [];

      const matchingTier = priceTiers.find(
        (tier) => tier.tierMin <= currentlyUsedSms && tier.tierMax >= currentlyUsedSms
      );

      if (matchingTier) {
        maxSmsNumberForCurrentTier = matchingTier.tierMax;
      }
    }

    const FREE_SMS_AMOUNT_IN_PREMIUM_PLAN = '500';
    const smsNumber = this.isFreeSmsAddon(addon)
      ? FREE_SMS_AMOUNT_IN_PREMIUM_PLAN
      : NumberFormat.formatWithThousandsSeparator(maxSmsNumberForCurrentTier || currentlyUsedSms);

    return this.t('application-layer.order-summary.sms-addon-label', { smsNumber });
  }

  private isFreeSmsAddon(addon: ReturnType<Rating['getAddons']>[0]) {
    const freeSmsTierFromPremium =
      addon.id === ADD_ON_IDS.SMS &&
      addon.fullPrice.priceAmount + addon.listPrice.priceAmount + addon.monthlyCost.priceAmount === 0;

    return freeSmsTierFromPremium;
  }

  private getAddonSecondaryLabels(rating: Rating, addon: ReturnType<Rating['getAddons']>[0]) {
    return [
      this.t('application-layer.order-summary.addon-secondary-label', {
        periodLabel: this.sharedTranslationService.getPeriodLabel(rating),
        price: addon.listPrice.format(rating.getCurrency()),
        period: addon.period,
      }),
    ];
  }

  private getAddOnDiscountPriceLabel(rating: Rating, addon: ReturnType<Rating['getAddons']>[0]) {
    return `-${addon.monthlyDiscount.multiply(rating.getPaymentPeriod()).format(rating.getCurrency())}`;
  }

  private getIsContinuingPlan(rating: Rating) {
    return rating.getPaymentPeriod() === this.CONTINUE_PAYMENT_PLAN;
  }

  private getPlanSecondaryLabels(rating: Rating, plan) {
    const periodLabel = this.sharedTranslationService.getPeriodLabel(rating);

    const price = rating.getPlanBaseMonthly().format(rating.getCurrency());
    const period = rating.getPaymentPeriod();
    const { tierMin, tierMax } = plan.currentTier;
    const labels: string[] = [];

    if (!this.isSocialStarter) {
      labels.push(this.sharedTranslationService.getContactsTierLabel(tierMin, tierMax));
    }

    labels.push(
      this.t('application-layer.order-summary.period-plan-secondary-label', {
        periodLabel,
        price,
        period,
      })
    );

    return labels;
  }
}
